<!-- eslint-disable vue/multi-word-component-names -->
<template>
    <iframe
        v-if="enabled"
        id="sfchat-iframe"
        :style="style"
        :src="iframeSource"
    />
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { LOCALE_DEFAULT } from '@model/const/locales.ts';
import { countries, pageRegex, getEnableAfter } from './constants.js';

export default {
    data() {
        return {
            style: '',
            enabled: false,
            country: null,
            iframeSource: '',
        };
    },
    computed: {
        ...mapState({
            location: (state) => state.geolocation?.location || null,
        }),
        ...mapGetters('config', ['$config']),
        locale() {
            return this.$store.state.geolocation.finalLocale || LOCALE_DEFAULT;
        },
        enabledAfter() {
            if (typeof window === 'undefined') return 0;

            if (localStorage.getItem('acronis_sfchat_maximized') === '1') {
                return 0;
            }

            return getEnableAfter(window.location.pathname);
        },
    },
    async mounted() {
        await this.setGeolocation();

        if (!this.checkEligibility()) return;

        setTimeout(() => {
            this.enabled = true;
            this.iframeSource = `/public/integrations/sfchat?lang=${this.locale.slice(0, 2)}`;
        }, this.enabledAfter);
        window.addEventListener('message', this.onMessageReceive);
    },
    beforeUnmount() {
        window.removeEventListener('message', this.onMessageReceive);
    },
    methods: {
        async setGeolocation() {
            if (this.location) return;
            await this.$store.dispatch('geolocation/getGeoLocation');
            this.country = (this.location?.data?.country?.code || '').toUpperCase();
        },
        isAllowedOrigin(origin) {
            try {
                const url = new URL(origin);
                const hostname = url.hostname;

                const ALLOWED_HOSTS = [
                    'service.force.com',
                    'acronis.com',
                    'my.salesforce.com',
                    'salesforce.com',
                ];

                return (
                    ALLOWED_HOSTS.some(allowed =>
                        hostname === allowed || hostname.endsWith(`.${allowed}`),
                    )
                );
            } catch (err) {
                return false;
            }
        },

        onMessageReceive(e) {
            if (!e.data.sender || e.data.sender !== 'sf_iframe') return;
            if (!this.isAllowedOrigin(e.origin)) return;


            if (e.data.message === 'onHelpButtonClick' || e.data.message === 'afterMaximize') {
                localStorage.setItem('acronis_sfchat_maximized', 1);
                this.style = 'height: 570px; width: 400px;';
            } else if (e.data.message === 'afterMinimize' || e.data.message === 'afterDestroy') {
                localStorage.setItem('acronis_sfchat_maximized', 0);
                this.style = 'height: 90px; width: 110px';
            } else if (e.data.message === 'onLoad') {
                const safeSliceData = JSON.parse(JSON.stringify(this.$store.state.slices.items?.['s-chat-settings'] || {}));
                try {
                    e?.source?.postMessage?.(
                        {
                            message: 'callback',
                            sender: 'sf_chat_interpreter',
                            sliceData: safeSliceData,
                        },
                        e.origin,
                    );
                } catch {
                    // Avoid sentry errors
                }
            } else {
                this.style = '';
            }
        },
        checkEligibility() {
            const routeEligible = pageRegex.some((reg) => reg.test(window.location.pathname));
            const countryEligible = this.country && !countries.includes(this.country);

            return countryEligible && routeEligible;
        },


    },
    async serverPrefetch() {
        if (!import.meta.env.SSR) return;

        await this.$store.dispatch('slices/getSyncedData', { slice: 's-chat-settings', locale: this.locale });
    },
};
</script>
<style scoped>
#sfchat-iframe {
    border: none;
    bottom: 0;
    inset-inline-end: 0;
    position: fixed;
    height: 90px;
    width: 110px;
    z-index: 500;
    overflow: hidden;
}
</style>
